import React, { useState, useEffect } from 'react';
import healthBg from '../assets/health_bento_bg.png';
import { Activity, Flame, ChevronRight, Droplet, Activity as ActivityIcon, Footprints, Info, Scale, ActivitySquare, TrendingDown, TrendingUp, Moon, Heart, HeartPulse, Dumbbell, Wind, BrainCircuit } from 'lucide-react';
import { Health } from '@capgo/capacitor-health';
import { Capacitor } from '@capacitor/core';
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
import DailyFeedbackWidget from './DailyFeedbackWidget';
import UnifiedBodyCompositionCard from './UnifiedBodyCompositionCard';

const extractVal = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.value ?? val.val ?? val.current ?? null;
  return val;
};

const HealthSidebar = ({ 
  healthData, 
  appleHealthConnected: propAppleHealthConnected, 
  googleHealthConnected: propGoogleHealthConnected,
  withingsConnected: propWithingsConnected,
  sleepData, 
  rhrData, 
  hrvData, 
  withingsDebug, 
  isGridLayout = false 
}) => {
  const [liveSteps, setLiveSteps] = useState(null);
  const [liveCalories, setLiveCalories] = useState(null);

  const isAppleConnected = healthData?.appleHealthConnected ?? propAppleHealthConnected ?? false;
  const isGoogleConnected = healthData?.googleHealthConnected ?? propGoogleHealthConnected ?? false;
  const isWithingsConnected = healthData?.withingsConnected ?? propWithingsConnected ?? false;

  useEffect(() => {
    if (Capacitor.isNativePlatform() && isAppleConnected) {
      const fetchLiveToday = async () => {
        try {
          const start = new Date();
          start.setHours(0, 0, 0, 0);
          const end = new Date();
          end.setHours(23, 59, 59, 999);
          
          const stepsData = await Health.queryAggregated({ dataType: 'steps', startDate: start.toISOString(), endDate: end.toISOString(), bucket: 'day', aggregation: 'sum' });
          
          if (stepsData && stepsData.samples && stepsData.samples.length > 0) {
            setLiveSteps(stepsData.samples[0].value);
          }
          
          try {
            if (!stepsData || !stepsData.samples || stepsData.samples.length === 0) {
              const rawSteps = await Health.readSamples({ dataType: 'steps', startDate: start.toISOString(), endDate: end.toISOString() });
              if (rawSteps && rawSteps.samples && rawSteps.samples.length > 0) {
                const manualSum = rawSteps.samples.reduce((acc, curr) => acc + curr.value, 0);
                setLiveSteps(manualSum);
              }
            }
          } catch(e) {
            console.error("Error manual sum steps:", e);
          }
          
          let calData;
          try {
            calData = await Health.queryAggregated({ dataType: 'calories', startDate: start.toISOString(), endDate: end.toISOString(), bucket: 'day', aggregation: 'sum' });
          } catch(e) {
            console.warn('Fallback calories query failed', e);
          }
          if (calData && calData.samples && calData.samples.length > 0) {
            setLiveCalories(calData.samples[0].value);
          }
        } catch (e) {
          console.error("Error fetching live health:", e);
        }
      };
      fetchLiveToday();
    }
  }, [isAppleConnected]);

  if (!healthData || healthData.error) return <div className="dashboard-sidebar">Cargando biometría...</div>;

  const { weight, bodyFat, hydration, activity, composition, cardio, bloodPressure } = healthData;

  const healthSourceLabel = isGoogleConnected 
    ? 'Google Health' 
    : (isAppleConnected 
        ? 'Apple Health' 
        : (isWithingsConnected ? 'Withings' : 'Sin sincronizar'));

  const wCurrent = extractVal(weight?.current);
  const wPrevious = extractVal(weight?.previous);
  const fCurrent = extractVal(bodyFat?.current);
  const fPrevious = extractVal(bodyFat?.previous);

  const weightTrend = wCurrent != null && wPrevious != null && wCurrent < wPrevious ? 'down' : 'up';
  const fatTrend = fCurrent != null && fPrevious != null && fCurrent < fPrevious ? 'down' : 'up';
  
  const compData = composition || {};
  const cardioData = cardio || {};
  
  const wHistory = weight?.history || [];
  const weightChartData = wHistory.map((w, i) => {
    const daysAgo = wHistory.length - 1 - i;
    let label = 'Hoy';
    if (daysAgo === 1) label = 'Ayer';
    if (daysAgo > 1) label = `Hace ${daysAgo} días`;
    return { day: i, label, weight: extractVal(w) };
  });
  // Calcular currentSleep priorizando duration si existe, o sumando las fases
  const stagesSum = sleepData?.stages && (sleepData.stages.deep || sleepData.stages.light || sleepData.stages.rem)
    ? (sleepData.stages.deep + sleepData.stages.light + sleepData.stages.rem)
    : null;
  const currentSleep = sleepData?.duration != null
    ? parseFloat(sleepData.duration)
    : (stagesSum !== null ? stagesSum : (sleepData?.history?.length > 0 ? sleepData.history[0] : null));
  const sleepTrend = currentSleep != null ? (currentSleep >= 7.5 ? 'good' : 'poor') : null;

  // RHR details
  const currentRhr = rhrData?.current ?? (rhrData?.history?.length > 0 ? rhrData.history[0] : null);
  const rhrTrend = currentRhr != null && rhrData?.average != null ? (currentRhr < rhrData.average ? 'down' : 'up') : 'up';

  // Steps details
  const backendSteps = activity?.currentSteps ?? activity?.steps ?? 0;
  const rawCurrentSteps = Math.round(liveSteps !== null ? liveSteps : backendSteps);
  
  const stepsData = {
    currentSteps: rawCurrentSteps,
    stepsGoal: activity?.stepsGoal ?? 10000,
    previousSteps: Math.round(activity?.previousSteps ?? 0),
    history: Array.isArray(activity?.history) ? activity.history : [],
    activeCalories: Math.round(liveCalories !== null ? liveCalories : (activity?.activeCalories || 0)),
    activeDurationFormated: activity?.activeDurationFormated || "00:00:00"
  };

  const stepsTrend = stepsData.currentSteps >= stepsData.previousSteps ? 'up' : 'down';
  const stepsPct = stepsData.stepsGoal > 0 ? (stepsData.currentSteps / stepsData.stepsGoal) * 100 : 0;
  const stepsChartData = stepsData.history.length > 0 ? stepsData.history.map((s, i) => {
    const daysAgo = stepsData.history.length - 1 - i;
    let label = 'Hoy';
    if (daysAgo === 1) label = 'Ayer';
    if (daysAgo > 1) label = `Hace ${daysAgo} días`;
    return { day: i, label, steps: s };
  }) : [];

  // NEW: Blood Pressure Data (Withings)
  const bpData = bloodPressure || {
    systolic: { current: null, previous: null, history: [] },
    diastolic: { current: null, previous: null, history: [] }
  };
  const sysVal = extractVal(bpData.systolic?.current);
  const diaVal = extractVal(bpData.diastolic?.current);
  const sysPrev = extractVal(bpData.systolic?.previous);
  
  const bpSystolicTrend = sysVal != null && sysPrev != null && sysVal <= sysPrev ? 'down' : 'up';
  
  const getBpStatus = (sys, dia) => {
    if (sys == null || dia == null) return { label: 'Sin datos', color: '#94a3b8', desc: 'Esperando registros...' };
    if (sys < 120 && dia < 80) return { label: 'Óptima ✓', color: 'var(--health-green)', desc: 'Presión arterial en rango ideal de salud.' };
    if (sys < 130 && dia < 85) return { label: 'Normal 🟢', color: '#84cc16', desc: 'Rango saludable de presión arterial.' };
    return { label: 'Elevada ⚠️', color: '#f59e0b', desc: 'Presión arterial ligeramente alta. Vigilar fatiga.' };
  };
  const bpStatus = getBpStatus(sysVal, diaVal);
  const sysHist = bpData.systolic?.history || [];
  const diaHist = bpData.diastolic?.history || [];
  const bpChartData = sysHist.map((sys, i) => {
    const daysAgo = sysHist.length - 1 - i;
    let label = 'Hoy';
    if (daysAgo === 1) label = 'Ayer';
    if (daysAgo > 1) label = `Hace ${daysAgo} días`;
    return {
      day: i,
      label,
      systolic: extractVal(sys),
      diastolic: extractVal(diaHist[i]) || 75
    };
  });

  // NEW: Sleep Breathing Rate Data (Withings Sleep)
  const breathingData = sleepData?.breathingRate || {
    current: null,
    average: null,
    history: []
  };
  const breathingChartData = (breathingData.history || []).map((b, i) => ({ day: i, breathing: b }));

  const containerClass = isGridLayout ? "health-sidebar-grid" : "dashboard-sidebar";

  return (
    <div className={containerClass}>
      
      <div className="health-span-full" style={{ marginBottom: '1rem' }}>
        <UnifiedBodyCompositionCard healthData={healthData} healthSourceLabel={healthSourceLabel} />
      </div>



      {/* 💓 CARDIOVASCULAR BLOOD PRESSURE CARD (Solo visible si hay datos reales) */}
      {sysVal !== null && (
        <div className="glass-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 className="text-muted text-sm text-gradient-health" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <HeartPulse size={16} /> Presión Arterial
            </h3>
            {bpData.systolic.current !== null ? (
              <span style={{ fontSize: '0.65rem', background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                Manual
              </span>
            ) : (
              <span style={{ fontSize: '0.65rem', background: 'rgba(6,182,212,0.15)', color: 'var(--health-cyan)', padding: '2px 6px', borderRadius: '4px' }}>{healthSourceLabel}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <div className="text-2xl" style={{ fontWeight: 700 }}>{sysVal}<span className="text-sm text-muted">/{diaVal !== null ? diaVal : '--'}</span></div>
            <div className="text-xs text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
              <Activity size={12} style={{ color: bpStatus.color }} /> {bpStatus.label}
            </div>
          </div>
          <div className="text-xs text-muted" style={{ marginBottom: '0.6rem' }}>
            Tendencia: {bpSystolicTrend === 'down' ? '📉 Sistólica estable' : '📈 Sistólica elevada'}
          </div>
          
          <div style={{ height: '40px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
              <AreaChart data={bpChartData}>
                <defs>
                  <linearGradient id="colorSys" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--health-cyan)" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="var(--health-cyan)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <YAxis domain={['dataMin - 10', 'dataMax + 10']} hide />
                <Area type="monotone" dataKey="systolic" stroke="var(--health-cyan)" strokeWidth={1.5} fillOpacity={1} fill="url(#colorSys)" />
                <Line type="monotone" dataKey="diastolic" stroke="#38bdf8" strokeWidth={1} strokeDasharray="3 3" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 🌬️ NEW: NIGHTTIME BREATHING RATE CARD */}
      <div className="glass-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 className="text-muted text-sm text-gradient-health" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Wind size={16} /> Frecuencia Respiratoria
          </h3>
          <span style={{ fontSize: '0.65rem', background: 'rgba(6,182,212,0.15)', color: 'var(--health-cyan)', padding: '2px 6px', borderRadius: '4px' }}>{healthSourceLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span className="text-3xl" style={{ fontWeight: 700 }}>
            {breathingData.current !== null && breathingData.current !== undefined ? breathingData.current : '--'} <span className="text-sm text-muted">rpm</span>
          </span>
          <span className="text-xs text-muted" style={{ color: breathingData.current ? (breathingData.current >= 12 && breathingData.current <= 16 ? 'var(--health-green)' : '#f59e0b') : 'var(--text-secondary)', fontSize: '0.7rem', fontWeight: 600 }}>
            {breathingData.current ? (breathingData.current >= 12 && breathingData.current <= 16 ? '✓ Fisiología Estable' : '⚠️ Ritmo Alterado') : 'Sin datos'}
          </span>
        </div>
        <div className="text-xs text-muted" style={{ marginBottom: '0.6rem' }}>
          Promedio nocturno: {breathingData.average !== null && breathingData.average !== undefined ? breathingData.average : '--'} rpm
        </div>

        <div style={{ height: '35px', width: '100%', marginTop: '0.2rem' }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <AreaChart data={breathingChartData}>
              <defs>
                <linearGradient id="colorBreath" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <YAxis domain={['dataMin - 1', 'dataMax + 1']} hide />
              <Area type="monotone" dataKey="breathing" stroke="#34d399" strokeWidth={1.5} fillOpacity={1} fill="url(#colorBreath)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 🚶 PASOS DIARIOS CARD */}
      <div className="glass-panel glass-panel-hover">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 className="text-muted text-sm text-gradient-health" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Footprints size={16} /> Pasos Diarios
          </h3>
          <span style={{ fontSize: '0.65rem', background: 'rgba(6,182,212,0.15)', color: 'var(--health-cyan)', padding: '2px 6px', borderRadius: '4px' }}>{healthSourceLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span className="text-3xl" style={{ fontWeight: 700 }}>{(stepsData.currentSteps || 0).toLocaleString('es-ES')} <span className="text-sm text-muted" style={{ fontSize: '0.75rem' }}>/ {(stepsData.stepsGoal || 10000).toLocaleString('es-ES')}</span></span>
          <span className={stepsTrend === 'up' ? 'trend-positive text-xs' : 'trend-up text-xs'} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', color: stepsTrend === 'up' ? 'var(--health-green)' : '#f59e0b', fontSize: '0.7rem' }}>
            {stepsTrend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.round(stepsPct)}%
          </span>
        </div>
        
        {/* Progress bar and Chart */}
        <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', marginBottom: '0.6rem', marginTop: '0.4rem' }}>
          <div 
            style={{ 
              height: '100%', 
              width: `${Math.min(stepsPct, 100)}%`, 
              background: 'linear-gradient(90deg, #10b981, #00e5ff)',
              transition: 'width 0.5s ease-in-out'
            }} 
          />
        </div>
        <div style={{ height: '35px', width: '100%', marginTop: '0.2rem', marginBottom: '0.5rem' }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <AreaChart data={stepsChartData}>
              <defs>
                <linearGradient id="colorSteps" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <YAxis domain={['dataMin - 500', 'dataMax + 500']} hide />
              <Area type="monotone" dataKey="steps" stroke="#10b981" strokeWidth={1.5} fillOpacity={1} fill="url(#colorSteps)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="text-xs text-muted" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem', fontWeight: 500 }}>
          <span>🔥 {Math.round(stepsData.activeCalories)} kcal activas</span>
          <span>⏱️ {stepsData.activeDurationFormated}</span>
        </div>

      </div>

      {/* 🏃 VO2 MAX CARD */}
      {(() => {
        const vo2 = cardioData?.vo2max;
        const vo2Accent = vo2 && vo2 > 50 ? '#10b981' : '#00e5ff';
        return (
          <div style={{ alignSelf: 'start', height: '100%' }} className="glass-panel glass-panel-hover">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
              <h3 style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Activity size={13} style={{ color: vo2Accent }} /> Capacidad Aeróbica
              </h3>
              <span style={{ fontSize: '0.65rem', background: 'rgba(6,182,212,0.15)', color: 'var(--health-cyan)', padding: '2px 6px', borderRadius: '4px' }}>{healthSourceLabel}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '0.3rem' }}>
              <span style={{ fontSize: '2.6rem', fontWeight: 800, color: 'white', lineHeight: 1, fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em' }}>
                {vo2 !== undefined && vo2 !== null ? vo2 : '--'}
              </span>
              <span style={{ fontSize: '0.85rem', color: vo2Accent, fontWeight: 600 }}>VO₂ máx</span>
            </div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
              {vo2 ? (vo2 > 50 ? 'Nivel Alto (Excelente)' : 'Nivel Medio') : 'Sin datos recientes'}
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default HealthSidebar;
